import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { cn } from "@/utils/classnames";
import moment from "moment";
import { ArrowDownCircle, ArrowUpCircle, XCircle } from "lucide-react";
import { useTransactionDrawer } from "@/hooks/transaction/use-transaction-drawer";
import { TransactionStatusBadge } from "./transaction-status-badge";

function Loading() {
  return (
    <div>
      <div role="status" className="px-6 py-8 animate-pulse">
        <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-48 mb-4"></div>
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[360px] mb-2.5"></div>
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 mb-2.5"></div>
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[330px] mb-2.5"></div>
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[300px] mb-2.5"></div>
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[360px]"></div>
        <span className="sr-only">Loading...</span>
      </div>
      <div
        role="status"
        className=" p-4 space-y-4 border border-gray-200 divide-y divide-gray-200 rounded shadow animate-pulse dark:divide-gray-700 md:p-6 dark:border-gray-700"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24 mb-2.5"></div>
            <div className="w-32 h-2 bg-gray-200 rounded-full dark:bg-gray-700"></div>
          </div>
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-700 w-12"></div>
        </div>
        <div className="flex items-center justify-between pt-4">
          <div>
            <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24 mb-2.5"></div>
            <div className="w-32 h-2 bg-gray-200 rounded-full dark:bg-gray-700"></div>
          </div>
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-700 w-12"></div>
        </div>
        <div className="flex items-center justify-between pt-4">
          <div>
            <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24 mb-2.5"></div>
            <div className="w-32 h-2 bg-gray-200 rounded-full dark:bg-gray-700"></div>
          </div>
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-700 w-12"></div>
        </div>
        <div className="flex items-center justify-between pt-4">
          <div>
            <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24 mb-2.5"></div>
            <div className="w-32 h-2 bg-gray-200 rounded-full dark:bg-gray-700"></div>
          </div>
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-700 w-12"></div>
        </div>
        <div className="flex items-center justify-between pt-4">
          <div>
            <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24 mb-2.5"></div>
            <div className="w-32 h-2 bg-gray-200 rounded-full dark:bg-gray-700"></div>
          </div>
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-700 w-12"></div>
        </div>
        <span className="sr-only">Loading...</span>
      </div>
      <div
        role="status"
        className="absolute bottom-0 inset-x-0 p-4 space-y-4 border border-gray-200 divide-y divide-gray-200 rounded shadow animate-pulse dark:divide-gray-700 md:p-6 dark:border-gray-700"
      ></div>
    </div>
  );
}

function Error() {
  return (
    <div className="rounded-md bg-red-50 p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <XCircle className="h-5 w-5 text-red-400" aria-hidden="true" />
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-red-800">
            There was an error loading the transaction details.
          </h3>
          <div className="mt-2 text-sm text-red-700">
            <ul role="list" className="list-disc space-y-1 pl-5">
              <li>Make sure your internet connection is stable</li>
              <li>Try closing and opening the drawer again</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TransactionDrawer() {
  const {
    drawerData: transaction,
    open,
    handleClose,
    isLoading,
    isError,
  } = useTransactionDrawer();

  const getAmount = () => {
    const formatted = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(parseFloat(transaction?.amount));

    return transaction?.transaction_direction === "debit"
      ? formatted
      : `-${formatted}`;
  };

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={handleClose}>
          <div className="fixed inset-0" />

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500 sm:duration-700"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-500 sm:duration-700"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="relative pointer-events-auto w-screen max-w-2xl">
                    <div className=" pb-[120px] flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                      <div className="px-4 py-6 sm:px-6 bg-skin-primary">
                        <div className="flex items-start justify-between">
                          <Dialog.Title className="text-base font-semibold leading-6 text-white">
                            Transaction
                          </Dialog.Title>
                          <div className="ml-3 flex h-7 items-center">
                            <button
                              type="button"
                              className="relative rounded-md text-gray-50 hover:text-gray-300 focus:ring-2 focus:ring-white"
                              onClick={handleClose}
                            >
                              <span className="absolute -inset-2.5" />
                              <span className="sr-only">Close panel</span>
                              <XMarkIcon
                                className="h-6 w-6"
                                aria-hidden="true"
                              />
                            </button>
                          </div>
                        </div>
                      </div>
                      {/* Main */}
                      {isLoading ? (
                        <Loading />
                      ) : isError ? (
                        <Error />
                      ) : (
                        <div className=" divide-y divide-gray-200">
                          <div className="pb-6">
                            <div className="flex-col space-y-4 px-4 sm:px-6 min-h-full">
                              <div className="flex justify-between items-center mt-6 sm:flex-1">
                                <div className="flex items-center justify-between w-full">
                                  <div className="flex gap-x-2 items-center">
                                    {transaction?.transaction_direction ===
                                    "credit" ? (
                                      <ArrowDownCircle className="w-8 h-8 text-red-600" />
                                    ) : (
                                      <ArrowUpCircle className="w-8 h-8 text-green-600" />
                                    )}
                                    <h3 className="text-xl font-bold text-gray-900 sm:text-2xl">
                                      {getAmount()}
                                    </h3>
                                  </div>
                                  <div className="flex items-center gap-x-2">
                                    <TransactionStatusBadge
                                      status={transaction?.transaction_status}
                                    />
                                    {/* <InvoiceStatusBadge
                                      status={
                                        invoice?.invoice_info.invoice_status ||
                                        DRAFT_INVOICE_STATUS
                                      }
                                    /> */}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="px-4 py-5 sm:px-6 sm:py-0 sm:pb-8">
                            <>
                              {/* Transaction */}
                              <dl className="mt-6 grid grid-cols-1 text-sm leading-6 sm:grid-cols-2">
                                <div className=" sm:pr-4 flex gap-x-1">
                                  <dt className="inline text-gray-500">
                                    <p>Transaction date</p>
                                  </dt>{" "}
                                  <dd className="inline text-gray-700">
                                    <time>
                                      {transaction?.initiated_at &&
                                        moment(
                                          transaction?.initiated_at
                                        ).format("LL")}
                                    </time>
                                  </dd>
                                </div>
                                <div className="mt-2 sm:mt-0 sm:pl-4">
                                  {transaction?.transaction_date && (
                                    <>
                                      <dt className="inline text-gray-500">
                                        Completed on
                                      </dt>{" "}
                                      <dd className="inline text-gray-700">
                                        <time>
                                          {transaction?.transaction_date &&
                                            moment(
                                              transaction?.transaction_date
                                            ).format("LL")}
                                        </time>
                                      </dd>
                                    </>
                                  )}
                                </div>

                                <div className="mt-6 border-t border-gray-900/5 pt-6 sm:pr-4">
                                  <dt className="font-semibold text-gray-500">
                                    From
                                  </dt>
                                  <dd className="mt-2 text-gray-500">
                                    <span className="font-medium text-gray-900">
                                      {
                                        transaction?.transaction_from_description
                                      }
                                    </span>
                                  </dd>
                                </div>

                                <div className="mt-8 sm:mt-6 sm:border-t sm:border-gray-900/5 sm:pl-4 sm:pt-6">
                                  <dt className="font-semibold text-gray-500">
                                    To
                                  </dt>
                                  <dd className="mt-2 text-gray-500">
                                    <span className="font-medium text-gray-900">
                                      {transaction?.transaction_to_description}
                                    </span>
                                  </dd>
                                </div>
                                <div className="sm:col-span-1 mt-6 pt-6 sm:pr-4">
                                  <dt className="text-sm font-medium text-gray-500">
                                    Status
                                  </dt>
                                  <dd className="mt-1 text-sm text-gray-900 font-medium">
                                    <TransactionStatusBadge
                                      status={transaction?.transaction_status}
                                    />
                                  </dd>
                                </div>
                                <div className="sm:col-span-1 mt-8 sm:mt-6 sm:pl-4 sm:pt-6">
                                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                                    Invoice #
                                  </dt>
                                  <dd className="mt-1 text-sm text-gray-900 font-medium">
                                    {transaction?.invoice_number}
                                  </dd>
                                </div>
                                <div className="sm:col-span-1 mt-6 pt-6 sm:pr-4">
                                  <dt className="text-sm font-medium text-gray-500">
                                    Amount
                                  </dt>
                                  <dd className="mt-1 text-sm text-gray-900 font-medium">
                                    {getAmount()}
                                  </dd>
                                </div>
                                <div className="sm:col-span-1 mt-8 sm:mt-6 sm:pl-4 sm:pt-6">
                                  <dt className="text-sm font-medium text-gray-500">
                                    Type
                                  </dt>
                                  <dd className="mt-1 text-sm text-gray-900 font-medium">
                                    {transaction?.transaction_type &&
                                      transaction?.transaction_type !==
                                        "none" && (
                                        <span
                                          className={cn(
                                            "inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-600/20"
                                          )}
                                        >
                                          <span className="">
                                            {transaction?.transaction_type
                                              .replace(/^[-_]*(.)/, (_, c) =>
                                                c.toUpperCase()
                                              )
                                              .replace(
                                                /[-_]+(.)/g,
                                                (_, c) => " " + c.toUpperCase()
                                              )}
                                          </span>
                                        </span>
                                      )}
                                  </dd>
                                </div>
                              </dl>
                            </>
                          </div>
                        </div>
                      )}
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}
